import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import {
  useContextStatements,
  type ContextStatement as DbContextStatement,
} from "@/hooks/useContextStatements";
import {
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Download,
  Upload,
  GripVertical,
  Filter,
  CheckCircle2,
  Circle,
  Clock,
  Copy,
  Search,
  Undo2,
  Redo2,
  Flag,
  ArrowUpDown,
  Calendar,
  AlertCircle,
  Info,
  HelpCircle,
  Wand2,
  Ban,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type TodoStatus =
  | "not-started"
  | "in-progress"
  | "completed"
  | "blocked"
  | "other";
type FilterType = "all" | "active" | "completed";
type Priority = "low" | "medium" | "high";
type SortBy = "date" | "priority" | "status" | "manual";
type DueDateType = "hard" | "soft" | "contingent";

// Use the ContextStatement from the hook
type ContextStatement = DbContextStatement;

export interface ContextContainerHandle {
  getStatements: () => string[];
  addStatement: (text: string) => void;
  addStatements: (texts: string[]) => void;
  clearStatements: () => void;
}

interface ContextContainerProps {
  onStatementsChange?: (statements: string[]) => void;
}

// Sortable Item Component
interface SortableItemProps {
  statement: ContextStatement;
  isEditing: boolean;
  editText: string;
  isFocused: boolean;
  isSelected: boolean;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditTextChange: (text: string) => void;
  onStatusChange: (status: TodoStatus) => void;
  onPriorityChange: (priority: Priority) => void;
  onDueDateChange: (date: Date | null, type?: DueDateType) => void;
  onDelete: () => void;
  onFocus: () => void;
  onToggleSelect: () => void;
  getStatusIcon: (status: TodoStatus) => JSX.Element;
  getPriorityColor: (priority: Priority) => string;
}

function SortableItem({
  statement,
  isEditing,
  editText,
  isFocused,
  isSelected,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditTextChange,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onDelete,
  onFocus,
  onToggleSelect,
  getStatusIcon,
  getPriorityColor,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: statement.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`group transition-all cursor-pointer ${
        statement.status === "completed" ? "opacity-75 bg-muted/30" : ""
      } ${isDragging ? "z-50 shadow-lg" : ""} ${
        isFocused ? "ring-2 ring-primary ring-offset-2" : ""
      } ${isSelected ? "bg-primary/10 border-primary" : ""}`}
      onClick={onFocus}
      tabIndex={0}
    >
      <CardContent className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)}
              className="min-h-[60px] resize-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.stopPropagation();
                  onCancelEdit();
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={onCancelEdit}>
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={onSaveEdit}
                disabled={!editText.trim()}
              >
                <Check className="w-3 h-3 mr-1" />
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            {/* Selection Checkbox */}
            <div className="mt-0.5">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => {
                  onToggleSelect();
                }}
                onClick={(e) => e.stopPropagation()}
                className={`transition-opacity ${
                  isSelected
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              />
            </div>

            {/* Drag Handle */}
            <button
              className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
              {...attributes}
              {...listeners}
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4" />
            </button>

            {/* Status Toggle */}
            <div className="mt-0.5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="hover:opacity-70 transition-opacity"
                    title="Change status"
                  >
                    {getStatusIcon(statement.status)}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => onStatusChange("not-started")}
                  >
                    <Circle className="w-4 h-4 mr-2 text-muted-foreground" />
                    Not Started
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onStatusChange("in-progress")}
                  >
                    <Clock className="w-4 h-4 mr-2 text-blue-600" />
                    In Progress
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange("completed")}>
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                    Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange("blocked")}>
                    <Ban className="w-4 h-4 mr-2 text-red-600" />
                    Blocked
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange("other")}>
                    <FileText className="w-4 h-4 mr-2 text-purple-600" />
                    Other
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Priority Selector */}
            <div className="mt-0.5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="hover:opacity-70 transition-opacity"
                    title="Change priority"
                  >
                    <Flag
                      className={`w-4 h-4 ${getPriorityColor(
                        statement.priority
                      )}`}
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => onPriorityChange("low")}>
                    <Flag className="w-4 h-4 mr-2 text-gray-400" />
                    Low Priority
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPriorityChange("medium")}>
                    <Flag className="w-4 h-4 mr-2 text-yellow-600" />
                    Medium Priority
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPriorityChange("high")}>
                    <Flag className="w-4 h-4 mr-2 text-red-600" />
                    High Priority
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex-1">
              <p
                className={`text-sm whitespace-pre-wrap ${
                  statement.status === "completed"
                    ? "line-through text-muted-foreground"
                    : ""
                }`}
              >
                {statement.text}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span>{statement.createdAt.toLocaleString()}</span>
                {statement.completedAt && (
                  <span className="text-green-600">
                    ✓ {statement.completedAt.toLocaleString()}
                  </span>
                )}
                {statement.dueDate && (
                  <span
                    className={`flex items-center gap-1 ${
                      new Date() > statement.dueDate &&
                      statement.status !== "completed"
                        ? "text-red-600 font-semibold"
                        : "text-blue-600"
                    }`}
                  >
                    <Calendar className="w-3 h-3" />
                    {statement.dueDateType === "hard" && (
                      <span title="Hard deadline">
                        <AlertCircle className="w-3 h-3" />
                      </span>
                    )}
                    {statement.dueDateType === "soft" && (
                      <span title="Soft deadline">
                        <Info className="w-3 h-3" />
                      </span>
                    )}
                    {statement.dueDateType === "contingent" && (
                      <span title="Contingent deadline">
                        <HelpCircle className="w-3 h-3" />
                      </span>
                    )}
                    Due: {statement.dueDate.toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={
                      statement.dueDate ? "Change due date" : "Set due date"
                    }
                  >
                    <Calendar className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="p-2">
                    <input
                      type="date"
                      placeholder="Select date"
                      value={
                        statement.dueDate
                          ? statement.dueDate.toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        onDueDateChange(
                          value ? new Date(value) : null,
                          statement.dueDateType
                        );
                      }}
                      className="w-full mb-2 px-2 py-1 text-sm border rounded"
                    />
                    {statement.dueDate && (
                      <>
                        <div className="text-xs font-semibold mb-1 text-muted-foreground">
                          Deadline Type:
                        </div>
                        <DropdownMenuItem
                          onClick={() =>
                            onDueDateChange(statement.dueDate!, "hard")
                          }
                        >
                          <AlertCircle className="w-3 h-3 mr-2" />
                          <span
                            className={
                              statement.dueDateType === "hard"
                                ? "font-semibold"
                                : ""
                            }
                          >
                            Hard Deadline
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            onDueDateChange(statement.dueDate!, "soft")
                          }
                        >
                          <Info className="w-3 h-3 mr-2" />
                          <span
                            className={
                              statement.dueDateType === "soft"
                                ? "font-semibold"
                                : ""
                            }
                          >
                            Soft Deadline
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            onDueDateChange(statement.dueDate!, "contingent")
                          }
                        >
                          <HelpCircle className="w-3 h-3 mr-2" />
                          <span
                            className={
                              statement.dueDateType === "contingent"
                                ? "font-semibold"
                                : ""
                            }
                          >
                            Contingent
                          </span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="sm"
                onClick={onStartEdit}
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                title="Delete"
              >
                <Trash2 className="w-3 h-3 text-destructive" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const ContextContainer = forwardRef<
  ContextContainerHandle,
  ContextContainerProps
>(({ onStatementsChange }, ref) => {
  // Use database hook
  const {
    statements: dbStatements,
    loading,
    createStatement,
    updateStatement,
    deleteStatement,
    bulkUpdate,
    syncStatements,
  } = useContextStatements();

  // Local state for UI
  const [statements, setStatements] = useState<ContextStatement[]>([]);
  const [isSyncingFromDb, setIsSyncingFromDb] = useState(false);
  const [newStatementText, setNewStatementText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("manual");

  // Parse Todos dialog state
  const [showParseDialog, setShowParseDialog] = useState(false);
  const [parseText, setParseText] = useState("");
  const [isParsing, setIsParsing] = useState(false);

  // Diff-style preview state
  type ParseAction = "complete" | "add" | "ignore";
  interface ParsedItem {
    id: string;
    extractedText: string; // What was found in user input
    matchedTodoId?: string; // ID of existing todo this completes
    action: ParseAction; // What will happen
    newTodo?: ContextStatement; // New todo data if action is "add" or "complete"
  }
  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([]);

  // Sync dbStatements to local state
  useEffect(() => {
    if (!loading && dbStatements.length >= 0) {
      setIsSyncingFromDb(true);
      setStatements(dbStatements);
      // Reset flag after state update
      setTimeout(() => setIsSyncingFromDb(false), 0);
    }
  }, [dbStatements, loading]);

  // Undo/Redo history
  const [history, setHistory] = useState<ContextStatement[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper to save state to history
  const saveToHistory = (newStatements: ContextStatement[]) => {
    setStatements(newStatements);
    // Trim history if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);
    // Add new state
    newHistory.push(newStatements);
    // Limit history to last 50 states
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex((prev) => prev + 1);
    }
    setHistory(newHistory);
  };

  // Undo function
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const newStatements = history[newIndex];
      setStatements(newStatements);
      syncStatements(newStatements); // Sync to database
      toast.success("Undone");
    }
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const newStatements = history[newIndex];
      setStatements(newStatements);
      syncStatements(newStatements); // Sync to database
      toast.success("Redone");
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = statements.findIndex((item) => item.id === active.id);
      const newIndex = statements.findIndex((item) => item.id === over.id);
      const reordered = arrayMove(statements, oldIndex, newIndex);

      // Update orderIndex to match new positions
      const reorderedWithIndex = reordered.map((s, index) => ({
        ...s,
        orderIndex: index,
      }));

      // Bulk update order indices in database
      bulkUpdate(
        reorderedWithIndex.map((s, index) => ({
          id: s.id,
          updates: { orderIndex: index },
        }))
      );

      saveToHistory(reorderedWithIndex);
      onStatementsChange?.(reorderedWithIndex.map((s) => s.text));
      toast.success("Item reordered");
    }
  };

  // Initialize history with current statements on mount
  useEffect(() => {
    if (history.length === 0 && statements.length > 0) {
      setHistory([statements]);
      setHistoryIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Note: We don't auto-sync to database here anymore.
  // Database operations happen directly in action handlers.

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getStatements: () => statements.map((s) => s.text),
    addStatement: (text: string) => {
      handleAddStatement(text);
    },
    addStatements: (texts: string[]) => {
      const maxOrderIndex =
        statements.length > 0
          ? Math.max(...statements.map((s) => s.orderIndex))
          : -1;

      const newStatements = texts.map((text, index) => ({
        id: crypto.randomUUID(),
        text,
        status: "not-started" as TodoStatus,
        priority: "medium" as Priority,
        createdAt: new Date(),
        orderIndex: maxOrderIndex + index + 1,
      }));
      const updated = [...statements, ...newStatements];
      saveToHistory(updated);
      onStatementsChange?.(updated.map((s) => s.text));
      toast.success(
        `Added ${texts.length} context statement${texts.length > 1 ? "s" : ""}`
      );
    },
    clearStatements: () => {
      saveToHistory([]);
      onStatementsChange?.([]);
      toast.success("Context cleared");
    },
  }));

  const handleAddStatement = (text?: string) => {
    const statementText = text || newStatementText.trim();
    if (!statementText) return;

    const maxOrderIndex =
      statements.length > 0
        ? Math.max(...statements.map((s) => s.orderIndex))
        : -1;

    const newStatement: ContextStatement = {
      id: crypto.randomUUID(),
      text: statementText,
      status: "not-started",
      priority: "medium",
      createdAt: new Date(),
      orderIndex: maxOrderIndex + 1,
    };

    // Create in database
    createStatement(newStatement);

    const updated = [...statements, newStatement];
    saveToHistory(updated);
    setNewStatementText("");
    onStatementsChange?.(updated.map((s) => s.text));

    if (!text) {
      toast.success("Context statement added");
    }
  };

  const handleDeleteStatement = (id: string) => {
    // Delete from database
    deleteStatement(id);

    const updated = statements.filter((s) => s.id !== id);
    saveToHistory(updated);
    onStatementsChange?.(updated.map((s) => s.text));
    setSelectedIds(new Set());
    setFocusedId(null);
    toast.success("Statement removed");
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;

    // Delete from database
    selectedIds.forEach((id) => deleteStatement(id));

    const updated = statements.filter((s) => !selectedIds.has(s.id));
    saveToHistory(updated);
    onStatementsChange?.(updated.map((s) => s.text));
    setSelectedIds(new Set());
    setFocusedId(null);
    toast.success(
      `${selectedIds.size} item${selectedIds.size > 1 ? "s" : ""} removed`
    );
  };

  const handleToggleStatus = (id: string) => {
    const updated = statements.map((s) => {
      if (s.id !== id) return s;

      let newStatus: TodoStatus;
      if (s.status === "not-started") {
        newStatus = "in-progress";
      } else if (s.status === "in-progress") {
        newStatus = "completed";
      } else {
        newStatus = "not-started";
      }

      return {
        ...s,
        status: newStatus,
        completedAt: newStatus === "completed" ? new Date() : undefined,
      };
    });
    saveToHistory(updated);
    onStatementsChange?.(updated.map((s) => s.text));
  };

  const handleStatusChange = (id: string, status: TodoStatus) => {
    // Update in database
    updateStatement(id, {
      status,
      completedAt: status === "completed" ? new Date() : undefined,
    });

    const updated = statements.map((s) =>
      s.id === id
        ? {
            ...s,
            status,
            completedAt: status === "completed" ? new Date() : undefined,
          }
        : s
    );
    saveToHistory(updated);
    onStatementsChange?.(updated.map((s) => s.text));
  };

  const handlePriorityChange = (id: string, priority: Priority) => {
    // Update in database
    updateStatement(id, { priority });

    const updated = statements.map((s) =>
      s.id === id ? { ...s, priority } : s
    );
    saveToHistory(updated);
    onStatementsChange?.(updated.map((s) => s.text));
  };

  const handleDueDateChange = (
    id: string,
    dueDate: Date | null,
    dueDateType?: DueDateType
  ) => {
    // Update in database
    updateStatement(id, {
      dueDate: dueDate || undefined,
      dueDateType: dueDate ? dueDateType || "soft" : undefined,
    });

    const updated = statements.map((s) =>
      s.id === id
        ? {
            ...s,
            dueDate: dueDate || undefined,
            dueDateType: dueDate
              ? dueDateType || s.dueDateType || "soft"
              : undefined,
          }
        : s
    );
    saveToHistory(updated);
    onStatementsChange?.(updated.map((s) => s.text));
  };

  const handleStartEdit = (statement: ContextStatement) => {
    setEditingId(statement.id);
    setEditText(statement.text);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editText.trim()) return;

    // Update in database
    updateStatement(editingId, { text: editText.trim() });

    const updated = statements.map((s) =>
      s.id === editingId ? { ...s, text: editText.trim() } : s
    );
    saveToHistory(updated);
    setEditingId(null);
    setEditText("");
    onStatementsChange?.(updated.map((s) => s.text));
    toast.success("Statement updated");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  // Keyboard navigation handlers
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't handle keyboard shortcuts while editing
    if (editingId) {
      if (e.key === "Escape") {
        handleCancelEdit();
      }
      return;
    }

    const filteredIds = filteredStatements.map((s) => s.id);
    const currentIndex = focusedId ? filteredIds.indexOf(focusedId) : -1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filteredIds.length > 0) {
          const nextIndex = Math.min(currentIndex + 1, filteredIds.length - 1);
          setFocusedId(filteredIds[nextIndex]);
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (filteredIds.length > 0) {
          const prevIndex = Math.max(currentIndex - 1, 0);
          setFocusedId(filteredIds[prevIndex]);
        }
        break;

      case "Enter":
        if (focusedId) {
          e.preventDefault();
          const statement = statements.find((s) => s.id === focusedId);
          if (statement) handleStartEdit(statement);
        }
        break;

      case "Delete":
      case "Backspace":
        if (focusedId && !editingId) {
          e.preventDefault();
          if (selectedIds.size > 0) {
            handleDeleteSelected();
          } else {
            handleDeleteStatement(focusedId);
          }
        }
        break;

      case " ":
        if (focusedId && !editingId) {
          e.preventDefault();
          handleToggleStatus(focusedId);
        }
        break;

      case "a":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          setSelectedIds(new Set(filteredIds));
          toast.success("All items selected");
        }
        break;

      case "z":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          if (e.shiftKey) {
            handleRedo();
          } else {
            handleUndo();
          }
        }
        break;

      case "y":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleRedo();
        }
        break;

      case "f":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          // Focus search input
          const searchInput = document.querySelector(
            'input[placeholder*="Search"]'
          ) as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }
        break;

      case "Escape":
        if (selectedIds.size > 0) {
          e.preventDefault();
          setSelectedIds(new Set());
          setFocusedId(null);
        }
        break;
    }
  };

  const handleExport = () => {
    const data = JSON.stringify(statements, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `context-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Context exported");
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);
          if (Array.isArray(data)) {
            const imported = data.map((s: ContextStatement, index: number) => ({
              ...s,
              priority: s.priority || "medium", // Default to medium if not present
              orderIndex: s.orderIndex !== undefined ? s.orderIndex : index, // Preserve or assign order
              createdAt: new Date(s.createdAt),
              completedAt: s.completedAt ? new Date(s.completedAt) : undefined,
              dueDate: s.dueDate ? new Date(s.dueDate) : undefined,
            }));
            saveToHistory(imported);
            onStatementsChange?.(imported.map((s: ContextStatement) => s.text));
            toast.success("Context imported");
          }
        } catch (error) {
          toast.error("Invalid file format");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleCopyAsMarkdown = () => {
    // If items are selected, copy only those; otherwise copy filtered items
    const itemsToCopy =
      selectedIds.size > 0
        ? statements.filter((s) => selectedIds.has(s.id))
        : filteredStatements;

    if (itemsToCopy.length === 0) {
      toast.error("No items to copy");
      return;
    }

    const markdown = itemsToCopy
      .map((statement) => {
        let checkbox = "[ ]"; // not-started
        if (statement.status === "in-progress") {
          checkbox = "[~]";
        } else if (statement.status === "completed") {
          checkbox = "[x]";
        }

        let line = `- ${checkbox} ${statement.text}`;

        // Add completion timestamp for completed items
        if (statement.completedAt) {
          const date = statement.completedAt.toLocaleDateString();
          line += ` _(completed ${date})_`;
        }

        return line;
      })
      .join("\n");

    // Copy to clipboard
    navigator.clipboard
      .writeText(markdown)
      .then(() => {
        const source = selectedIds.size > 0 ? "selected" : "filtered";
        toast.success(
          `Copied ${itemsToCopy.length} ${source} item${
            itemsToCopy.length > 1 ? "s" : ""
          } as Markdown`
        );
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  const handleParseTodos = async () => {
    if (!parseText.trim()) {
      toast.error("Please enter some text to parse");
      return;
    }

    setIsParsing(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error("You must be logged in to parse todos");
        return;
      }

      // Pass existing todos for context-aware parsing
      const existingTodos = statements.map((s) => ({
        status: s.status,
        text: s.text,
      }));

      const response = await supabase.functions.invoke("parse-todos", {
        body: {
          userId: user.id,
          text: parseText,
          existingTodos,
          mode: "context-aware",
        },
      });

      console.log("Full response:", response);

      if (response.error) {
        console.error("Parse todos error:", response.error);
        console.error(
          "Error details:",
          JSON.stringify(response.error, null, 2)
        );

        // Try to read the response body
        if (
          response.error.context &&
          response.error.context instanceof Response
        ) {
          const errorBody = await response.error.context.text();
          console.error("Response body:", errorBody);
          try {
            const errorJson = JSON.parse(errorBody);
            toast.error(
              `Failed to parse todos: ${errorJson.error || "Unknown error"}`
            );
          } catch {
            toast.error(
              `Failed to parse todos: ${errorBody || response.error.message}`
            );
          }
        } else {
          toast.error(
            `Failed to parse todos: ${
              response.error.message || "Unknown error"
            }`
          );
        }
        return;
      }

      const { data, error } = response;

      if (data && data.todos && Array.isArray(data.todos)) {
        // Build diff-style preview
        const items: ParsedItem[] = data.todos.map(
          (
            todo: {
              text: string;
              category?: string; // AI's category: todo, in-progress, done, blocked, other
              priority?: string;
              status?: string; // AI's flexible status text
              dueDate?: string;
              dueDateType?: string;
              completedAt?: string;
              isUnplanned?: boolean;
            },
            index: number
          ) => {
            const maxOrderIndex =
              statements.length > 0
                ? Math.max(...statements.map((s) => s.orderIndex))
                : -1;

            // Map AI's category to database status
            let dbStatus: TodoStatus = "not-started";
            if (todo.category === "done") {
              dbStatus = "completed";
            } else if (todo.category === "in-progress") {
              dbStatus = "in-progress";
            } else if (todo.category === "blocked") {
              dbStatus = "blocked";
            } else if (todo.category === "other") {
              dbStatus = "other";
            } else if (todo.category === "todo") {
              dbStatus = "not-started";
            }

            const newTodo: ContextStatement = {
              id: crypto.randomUUID(),
              text: todo.text,
              status: dbStatus,
              statusDetail: todo.status, // Store AI's flexible status text
              priority: (todo.priority as Priority) || "medium",
              createdAt: new Date(),
              completedAt: dbStatus === "completed" ? new Date() : undefined,
              dueDate: todo.dueDate ? new Date(todo.dueDate) : undefined,
              dueDateType: (todo.dueDateType as DueDateType) || undefined,
              orderIndex: maxOrderIndex + index + 1,
            };

            // Try to find matching existing todo (fuzzy match)
            const matchedTodo = statements.find(
              (s) =>
                s.text
                  .toLowerCase()
                  .includes(todo.text.toLowerCase().substring(0, 20)) ||
                todo.text
                  .toLowerCase()
                  .includes(s.text.toLowerCase().substring(0, 20))
            );

            // Determine action
            let action: ParseAction = "add";
            if (matchedTodo && todo.status === "completed") {
              action = "complete"; // Will mark existing todo as complete
            } else if (todo.isUnplanned || !matchedTodo) {
              action = "add"; // New todo
            }

            return {
              id: crypto.randomUUID(),
              extractedText: todo.text,
              matchedTodoId: matchedTodo?.id,
              action,
              newTodo,
            };
          }
        );

        setParsedItems(items);
        toast.success(
          `Parsed ${data.totalParsed} item${data.totalParsed > 1 ? "s" : ""}`
        );
      } else {
        toast.error("No todos found in the text");
      }
    } catch (error) {
      console.error("Parse todos error:", error);
      toast.error("Failed to parse todos");
    } finally {
      setIsParsing(false);
    }
  };

  const handleConfirmParsedTodos = () => {
    if (parsedItems.length === 0) return;

    const toAdd: ContextStatement[] = [];
    const toComplete: string[] = [];

    parsedItems.forEach((item) => {
      if (item.action === "ignore") return;

      if (item.action === "complete" && item.matchedTodoId) {
        toComplete.push(item.matchedTodoId);
      } else if (item.action === "add" && item.newTodo) {
        toAdd.push(item.newTodo);
      }
    });

    // Mark matched todos as complete
    toComplete.forEach((id) => {
      const todo = statements.find((s) => s.id === id);
      if (todo) {
        updateStatement(id, { status: "completed", completedAt: new Date() });
      }
    });

    // Add new todos
    toAdd.forEach((todo) => {
      createStatement(todo);
    });

    const updated = statements.map((s) =>
      toComplete.includes(s.id)
        ? { ...s, status: "completed" as TodoStatus, completedAt: new Date() }
        : s
    );
    const final = [...updated, ...toAdd];

    saveToHistory(final);
    onStatementsChange?.(final.map((s) => s.text));

    toast.success(`Completed ${toComplete.length}, added ${toAdd.length} new`);

    // Reset dialog
    setShowParseDialog(false);
    setParseText("");
    setParsedItems([]);
  };

  // Filter statements based on selected filter and search query
  const filteredStatements = statements.filter((statement) => {
    // First apply status filter
    let passesFilter = true;
    if (filter === "active") passesFilter = statement.status !== "completed";
    else if (filter === "completed")
      passesFilter = statement.status === "completed";

    // Then apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const textMatch = statement.text.toLowerCase().includes(query);
      return passesFilter && textMatch;
    }

    return passesFilter;
  });

  // Sort filtered statements
  const sortedStatements = [...filteredStatements].sort((a, b) => {
    if (sortBy === "manual") {
      // Manual order - keep original array order
      return 0;
    } else if (sortBy === "priority") {
      // High > Medium > Low
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    } else if (sortBy === "status") {
      // In Progress > Not Started > Blocked > Completed > Other
      const statusOrder = {
        "in-progress": 0,
        "not-started": 1,
        blocked: 2,
        completed: 3,
        other: 4,
      };
      return statusOrder[a.status] - statusOrder[b.status];
    } else if (sortBy === "date") {
      // Newest first
      return b.createdAt.getTime() - a.createdAt.getTime();
    }
    return 0;
  });

  // Calculate stats
  const totalCount = statements.length;
  const completedCount = statements.filter(
    (s) => s.status === "completed"
  ).length;
  const activeCount = totalCount - completedCount;
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const getStatusIcon = (status: TodoStatus) => {
    if (status === "completed") {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    } else if (status === "in-progress") {
      return <Clock className="w-4 h-4 text-blue-600" />;
    } else if (status === "blocked") {
      return <Ban className="w-4 h-4 text-red-600" />;
    } else if (status === "other") {
      return <FileText className="w-4 h-4 text-purple-600" />;
    }
    return <Circle className="w-4 h-4 text-muted-foreground" />;
  };

  const getPriorityColor = (priority: Priority) => {
    if (priority === "high") return "text-red-600";
    if (priority === "low") return "text-gray-400";
    return "text-yellow-600";
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Loading State */}
      {loading && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading context...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-lg font-semibold">Context Container</h2>
            <p className="text-sm text-muted-foreground">
              Broader context statements and lists
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowParseDialog(true)}
              title="Parse Todos from Text (AI-powered)"
            >
              <Wand2 className="w-4 h-4 mr-1" />
              Parse
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              title="Redo (Ctrl+Shift+Z or Ctrl+Y)"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyAsMarkdown}
              disabled={filteredStatements.length === 0}
              title="Copy as Markdown"
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              title="Import context"
            >
              <Upload className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={statements.length === 0}
              title="Export context"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        {totalCount > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>
                {completedCount} of {totalCount} completed
              </span>
              <span>{progressPercent}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-green-600 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Add New Statement */}
      <div className="p-4 border-b bg-muted/30">
        <div className="flex gap-2">
          <Textarea
            value={newStatementText}
            onChange={(e) => setNewStatementText(e.target.value)}
            placeholder="Add a declarative statement or list item..."
            className="flex-1 min-h-[60px] resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                e.preventDefault();
                handleAddStatement();
              }
            }}
          />
          <Button
            onClick={() => handleAddStatement()}
            disabled={!newStatementText.trim()}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Ctrl+Enter to add • Stores broader context across analyses
        </p>
      </div>

      {/* Search and Filter Bar */}
      {statements.length > 0 && (
        <div className="px-4 py-2 border-b bg-muted/20">
          <div className="flex gap-2 items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filter items"
                  className="shrink-0"
                >
                  <Filter className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setFilter("all")}>
                  <span className={filter === "all" ? "font-semibold" : ""}>
                    All ({totalCount})
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter("active")}>
                  <span className={filter === "active" ? "font-semibold" : ""}>
                    Active ({activeCount})
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter("completed")}>
                  <span
                    className={filter === "completed" ? "font-semibold" : ""}
                  >
                    Completed ({completedCount})
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Sort items"
                  className="shrink-0"
                >
                  <ArrowUpDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setSortBy("manual")}>
                  <span className={sortBy === "manual" ? "font-semibold" : ""}>
                    Manual Order
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("priority")}>
                  <span
                    className={sortBy === "priority" ? "font-semibold" : ""}
                  >
                    By Priority
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("status")}>
                  <span className={sortBy === "status" ? "font-semibold" : ""}>
                    By Status
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("date")}>
                  <span className={sortBy === "date" ? "font-semibold" : ""}>
                    By Date (Newest)
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items... (Ctrl+F)"
                className="w-full pl-8 pr-8 py-1.5 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setSearchQuery("");
                  }
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {searchQuery && (
            <p className="text-xs text-muted-foreground mt-1">
              Found {filteredStatements.length} item
              {filteredStatements.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div className="px-4 py-2 border-b bg-primary/5 flex items-center justify-between">
          <span className="text-sm font-medium">
            {selectedIds.size} item{selectedIds.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedIds(new Set());
                toast.success("Selection cleared");
              }}
            >
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Statements List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {filteredStatements.length === 0 && statements.length === 0 ? (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-sm">No Context Yet</CardTitle>
                <CardDescription className="text-xs">
                  Add declarative statements and lists that provide broader
                  context for your analyses.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : filteredStatements.length === 0 ? (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-sm">No {filter} items</CardTitle>
                <CardDescription className="text-xs">
                  Change the filter to see other items.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedStatements.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3" onKeyDown={handleKeyDown}>
                  {sortedStatements.map((statement) => (
                    <SortableItem
                      key={statement.id}
                      statement={statement}
                      isEditing={editingId === statement.id}
                      isFocused={focusedId === statement.id}
                      isSelected={selectedIds.has(statement.id)}
                      editText={editText}
                      onStartEdit={() => handleStartEdit(statement)}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={handleCancelEdit}
                      onEditTextChange={setEditText}
                      onStatusChange={(status) =>
                        handleStatusChange(statement.id, status)
                      }
                      onPriorityChange={(priority) =>
                        handlePriorityChange(statement.id, priority)
                      }
                      onDueDateChange={(date, type) =>
                        handleDueDateChange(statement.id, date, type)
                      }
                      onDelete={() => handleDeleteStatement(statement.id)}
                      onFocus={() => setFocusedId(statement.id)}
                      onToggleSelect={() => {
                        const newSelected = new Set(selectedIds);
                        if (newSelected.has(statement.id)) {
                          newSelected.delete(statement.id);
                        } else {
                          newSelected.add(statement.id);
                        }
                        setSelectedIds(newSelected);
                      }}
                      getStatusIcon={getStatusIcon}
                      getPriorityColor={getPriorityColor}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </ScrollArea>

      {/* Footer Stats */}
      {statements.length > 0 && (
        <div className="border-t p-3 bg-muted/30 space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {statements.length} context statement
              {statements.length !== 1 ? "s" : ""}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm("Clear all context statements?")) {
                  setStatements([]);
                  setSelectedIds(new Set());
                  setFocusedId(null);
                  onStatementsChange?.([]);
                  toast.success("Context cleared");
                }
              }}
              className="h-7 text-xs"
            >
              Clear All
            </Button>
          </div>
          <div className="text-xs text-muted-foreground pt-1 border-t">
            <p className="font-medium mb-1">Keyboard Shortcuts:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <span>↑↓ Navigate</span>
              <span>Space Toggle</span>
              <span>Enter Edit</span>
              <span>Esc Cancel</span>
              <span>Ctrl+A Select all</span>
            </div>
          </div>
        </div>
      )}

      {/* Parse Todos Dialog */}
      <Dialog open={showParseDialog} onOpenChange={setShowParseDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Parse Todos from Text</DialogTitle>
            <DialogDescription>
              Paste any unstructured text (emails, notes, Slack messages, etc.)
              and AI will extract individual todo items with priorities and due
              dates.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4">
            {/* Input Section */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Paste your text here:
              </label>
              <Textarea
                value={parseText}
                onChange={(e) => setParseText(e.target.value)}
                placeholder="Example: 
- Fix the login bug ASAP
- Schedule meeting with Sarah next week
- Review PR when you get a chance
- Deploy to production by Friday"
                className="min-h-[200px] font-mono text-sm"
                disabled={isParsing}
              />
            </div>

            {/* Diff-Style Preview Section */}
            {parsedItems.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">
                    Review Changes (
                    {parsedItems.filter((i) => i.action !== "ignore").length}{" "}
                    actions)
                  </label>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="w-3 h-3" />
                      {
                        parsedItems.filter((i) => i.action === "complete")
                          .length
                      }{" "}
                      complete
                    </span>
                    <span className="flex items-center gap-1 text-blue-600">
                      <Plus className="w-3 h-3" />
                      {parsedItems.filter((i) => i.action === "add").length} add
                    </span>
                    <span className="flex items-center gap-1 text-gray-400">
                      <X className="w-3 h-3" />
                      {
                        parsedItems.filter((i) => i.action === "ignore").length
                      }{" "}
                      ignore
                    </span>
                  </div>
                </div>

                <ScrollArea className="h-[400px] border rounded-md">
                  <div className="divide-y">
                    {parsedItems.map((item, index) => {
                      const matchedTodo = item.matchedTodoId
                        ? statements.find((s) => s.id === item.matchedTodoId)
                        : null;

                      return (
                        <div
                          key={item.id}
                          className={`p-3 ${
                            item.action === "ignore"
                              ? "opacity-40 bg-gray-50"
                              : ""
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-3">
                            {/* LEFT: What user entered */}
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-muted-foreground">
                                #{index + 1} FROM INPUT:
                              </div>
                              <div className="text-sm font-mono bg-blue-50 p-2 rounded border-l-2 border-blue-600">
                                {item.extractedText}
                              </div>
                              {item.newTodo && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span
                                    className={`${
                                      item.newTodo.priority === "high"
                                        ? "text-red-600"
                                        : item.newTodo.priority === "low"
                                        ? "text-gray-400"
                                        : "text-yellow-600"
                                    }`}
                                  >
                                    <Flag className="w-3 h-3 inline" />{" "}
                                    {item.newTodo.priority}
                                  </span>
                                  <span>
                                    {item.newTodo.status === "completed"
                                      ? "✓"
                                      : item.newTodo.status === "in-progress"
                                      ? "~"
                                      : "○"}{" "}
                                    {item.newTodo.status}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* RIGHT: What will happen */}
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="text-xs font-medium text-muted-foreground">
                                  ACTION:
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                    >
                                      {item.action === "complete" &&
                                        "✓ Complete"}
                                      {item.action === "add" && "+ Add New"}
                                      {item.action === "ignore" && "× Ignore"}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const updated = [...parsedItems];
                                        updated[index].action = "complete";
                                        setParsedItems(updated);
                                      }}
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-2" />
                                      Mark Existing Complete
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const updated = [...parsedItems];
                                        updated[index].action = "add";
                                        updated[index].matchedTodoId =
                                          undefined;
                                        setParsedItems(updated);
                                      }}
                                    >
                                      <Plus className="w-4 h-4 mr-2" />
                                      Add as New Todo
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const updated = [...parsedItems];
                                        updated[index].action = "ignore";
                                        setParsedItems(updated);
                                      }}
                                    >
                                      <X className="w-4 h-4 mr-2" />
                                      Ignore This Item
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>

                              {item.action === "complete" && matchedTodo && (
                                <div className="text-sm bg-green-50 p-2 rounded border-l-2 border-green-600">
                                  <div className="flex items-center gap-2 mb-1">
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    <span className="font-medium">
                                      Will complete:
                                    </span>
                                  </div>
                                  <div className="text-muted-foreground">
                                    {matchedTodo.text}
                                  </div>
                                </div>
                              )}

                              {item.action === "add" && item.newTodo && (
                                <div className="text-sm bg-blue-50 p-2 rounded border-l-2 border-blue-600">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Plus className="w-4 h-4 text-blue-600" />
                                    <span className="font-medium">
                                      Will add new:
                                    </span>
                                  </div>
                                  <div className="text-muted-foreground">
                                    {item.newTodo.text}
                                  </div>
                                </div>
                              )}

                              {item.action === "ignore" && (
                                <div className="text-sm bg-gray-50 p-2 rounded border-l-2 border-gray-400">
                                  <div className="flex items-center gap-2">
                                    <X className="w-4 h-4 text-gray-400" />
                                    <span className="text-muted-foreground">
                                      Will be skipped
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Manual match selector */}
                              {item.action === "complete" && (
                                <div className="mt-2">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs w-full"
                                      >
                                        {matchedTodo
                                          ? "Change match..."
                                          : "Select todo to complete..."}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-64">
                                      {statements
                                        .filter((s) => s.status !== "completed")
                                        .map((s) => (
                                          <DropdownMenuItem
                                            key={s.id}
                                            onClick={() => {
                                              const updated = [...parsedItems];
                                              updated[index].matchedTodoId =
                                                s.id;
                                              setParsedItems(updated);
                                            }}
                                          >
                                            <div className="truncate">
                                              {s.text}
                                            </div>
                                          </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowParseDialog(false);
                setParseText("");
                setParsedItems([]);
              }}
              disabled={isParsing}
            >
              Cancel
            </Button>
            {parsedItems.length === 0 ? (
              <Button
                onClick={handleParseTodos}
                disabled={isParsing || !parseText.trim()}
              >
                {isParsing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Parsing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Parse with AI
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleConfirmParsedTodos}>
                <Check className="w-4 h-4 mr-2" />
                Apply Changes
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

ContextContainer.displayName = "ContextContainer";
